<template>
  <div>
    <v-container grid-list-md>
      <!-- HEADER -->
      <v-layout row wrap>
        <v-flex xs12>
          <v-btn icon @click="$router.back()">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <h1 class="d-inline ml-2">{{ $t('security_assign_guards_to_park') }}</h1>
        </v-flex>
      </v-layout>

      <!-- PARK INFO -->
      <v-layout row wrap v-if="parkDetail">
        <v-flex xs12>
          <v-card class="elevation-2 mb-3">
            <v-card-text>
              <v-layout row>
                <v-flex xs12 sm6>
                  <h3>{{ parkDetail.park_name }}</h3>
                  <p>
                    <strong>{{ $t('status') }}:</strong>
                    <v-chip :color="getStatusColor(parkDetail.status)" text-color="white" small>
                      {{ parkDetail.status_display || parkDetail.status }}
                    </v-chip>
                  </p>
                </v-flex>
                <v-flex xs12 sm6 class="text-right">
                  <p>
                    <strong>{{ $t('created_at') }}:</strong>
                    {{ formatDate(parkDetail.created_at) }}
                  </p>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- LOADING STATE -->
      <v-layout row wrap v-if="isSecurityLoading">
        <v-flex xs12>
          <v-progress-linear indeterminate></v-progress-linear>
        </v-flex>
      </v-layout>

      <!-- ERROR STATE -->
      <v-layout row wrap v-if="getSecurityError">
        <v-flex xs12>
          <v-alert type="error" dismissible>
            {{ getSecurityError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- GUARDS LIST -->
      <v-layout row wrap v-if="guards.length > 0 && !isSecurityLoading">
        <v-flex xs12>
          <h2>{{ $t('security_available_guards') }}</h2>
        </v-flex>

        <v-flex xs12 sm6 md4 v-for="guard in guards" :key="guard.id">
          <v-card :class="['elevation-2', guard.assigned_park === parkId ? 'assigned' : '']">
            <v-card-title primary-title>
              <div>
                <h3 class="headline mb-0">{{ guard.first_name }} {{ guard.last_name }}</h3>
                <small>{{ guard.username }}</small>
              </div>
            </v-card-title>
            <v-card-text>
              <v-list dense>
                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('badge_number') }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ guard.badge_number || 'N/A' }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile v-if="guard.assigned_park">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('assigned_park') }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      <v-chip small color="primary" text-color="white">
                        {{ guard.assigned_park_name }}
                      </v-chip>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile v-if="guard.assigned_company">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('assigned_company') }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      <v-chip small color="info" text-color="white">
                        {{ guard.assigned_company_name }}
                      </v-chip>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile v-if="!guard.assigned_park && !guard.assigned_company">
                  <v-list-tile-content>
                    <v-list-tile-title class="success--text">
                      {{ $t('available') }}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </v-list-tile>

                <v-list-tile>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ $t('status') }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      <v-chip :color="guard.is_active ? 'green' : 'grey'" text-color="white" small>
                        {{ guard.is_active ? $t('active') : $t('inactive') }}
                      </v-chip>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </v-list>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn v-if="guard.assigned_park !== parkId"
                     color="primary" 
                     flat 
                     @click="assignGuard(guard)"
                     :loading="loadingGuardId === guard.id"
                     :disabled="guard.assigned_company !== null && guard.assigned_company !== undefined">
                {{ $t('security_assign') }}
              </v-btn>
              <v-btn v-if="guard.assigned_park === parkId"
                     color="error" 
                     flat 
                     @click="unassignGuard(guard)"
                     :loading="loadingGuardId === guard.id">
                {{ $t('security_unassign') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- EMPTY STATE -->
      <v-layout row wrap v-if="guards.length === 0 && !isSecurityLoading">
        <v-flex xs12>
          <v-alert type="info">
            {{ $t('security_no_guards_available') }}
          </v-alert>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { FETCH_GUARDS, ASSIGN_GUARD_TO_PARK, FETCH_MY_PARKS } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'

export default {
  name: 'AssignGuardsToPark',
  data() {
    return {
      parkId: null,
      loadingGuardId: null
    }
  },
  computed: {
    ...mapGetters(['getToken']),
    ...mapGetters('security', ['getGuards', 'getParkDetail', 'isSecurityLoading', 'getSecurityError']),
    guards() {
      return this.getGuards || []
    },
    parkDetail() {
      return this.getParkDetail
    }
  },
  methods: {
    getStatusColor(status) {
      const colors = {
        'ACTIVE': 'green',
        'PENDING': 'warning',
        'REJECTED': 'error',
        'INACTIVE': 'grey'
      }
      return colors[status] || 'grey'
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString()
    },
    assignGuard(guard) {
      this.loadingGuardId = guard.id
      this.$store.dispatch(`security/${ASSIGN_GUARD_TO_PARK}`, {
        token: this.getToken,
        guardId: guard.id,
        parkId: this.parkId
      })
      .then(() => {
        this.$store.commit('snackbar_change_message', this.$t('security_guard_assigned_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_assigning_guard'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.loadingGuardId = null
      })
    },
    unassignGuard(guard) {
      this.loadingGuardId = guard.id
      this.$store.dispatch(`security/${ASSIGN_GUARD_TO_PARK}`, {
        token: this.getToken,
        guardId: guard.id,
        parkId: null
      })
      .then(() => {
        this.$store.commit('snackbar_change_message', this.$t('security_guard_unassigned_success'))
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchData()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', this.$t('error_unassigning_guard'))
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.loadingGuardId = null
      })
    },
    fetchData() {
      this.$store.dispatch(`security/${FETCH_GUARDS}`, this.getToken)
      this.$store.dispatch(`security/${FETCH_MY_PARKS}`, this.getToken)
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    this.parkId = parseInt(this.$route.params.parkId)

    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.fetchData()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.ml-2 {
  margin-left: 8px;
}
.d-inline {
  display: inline-block;
}
.mb-3 {
  margin-bottom: 20px;
}
.text-right {
  text-align: right;
}
.assigned {
  border-left: 4px solid #4caf50;
}
</style>
